import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Paper,
    Skeleton,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Tabs,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import { Download } from '@mui/icons-material';
import { exportAgreementCsv, getAgreement } from '../../services/researchService';
import { listRuns } from '../../services/runService';
import { saveFile } from '../../services/download';
import { RC_NAMES, UT_NAMES } from '../teacher/reportStats';
import { BackToRuns } from '../runs/parts';

const fmtK = (k) => (typeof k === 'number' ? k.toFixed(2) : '—');
const pct = (a) => (typeof a === 'number' ? `${Math.round(a * 100)}%` : '—');
const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('en-US') : '—');

const ROWS = {
    human_human: { label: 'Human–human (ceiling)', on: (r) => `Reliability subset · ${r.lessons ?? '—'} lessons, two annotators` },
    model_human: { label: 'Model–human', on: (r, ref) => `Classifier (CCDH, 5-class RC) vs. ${ref} label` },
    llm_human: { label: 'LLM–human', on: (r, ref) => `Zero-shot LLM, mean of both models, vs. ${ref} label` },
    llm_human_model: { label: (r) => `· ${r.model} alone`, on: () => '' },
    classifier_llm: { label: 'Classifier–LLM', on: () => 'No human label involved' },
    reannotators_flagged: { label: 'Re-annotators · flagged items', on: () => 'Two blind codes per item (CD, HU, HD)' },
    reannotators_audit: { label: 'Re-annotators · audit sample', on: () => 'Two blind codes per random unflagged item' },
};

const WAITING = {
    no_reliability_pair: (w) => `Not yet: no reliability-subset lesson has both annotations submitted (${w.done ?? 0} of ${w.of ?? 0})`,
    no_blind_pair: () => 'Not yet: no adjudication item has both blind codes',
    model: () => 'Not yet: needs the retrained 5-class model (YES-205)',
};

// κ from 0 to 1 as a bar, with the human–human ceiling as a dark tick
const KappaBar = ({ kappa, ceiling }) => (
    <Box aria-hidden sx={{ position: 'relative', width: 90, height: 6, borderRadius: 999, bgcolor: 'action.selected', display: 'inline-block', verticalAlign: 'middle', ml: 1 }}>
        <Box sx={{ position: 'absolute', inset: 0, width: `${Math.max(0, kappa) * 100}%`, borderRadius: 999, bgcolor: 'primary.main' }} />
        {typeof ceiling === 'number' && <Box sx={{ position: 'absolute', top: -3, bottom: -3, left: `calc(${ceiling * 100}% - 1px)`, width: 2, bgcolor: 'text.primary' }} />}
    </Box>
);

// Every run is listed; nothing marks a run as practice, so only the runs picked count
const ChooseRuns = ({ runs, picked, onApply, onClose }) => {
    const [chosen, setChosen] = useState(new Set(picked));
    const rows = runs.filter((r) => chosen.has(r.id));
    const lessons = rows.reduce((n, r) => n + (r.lessons || 0), 0);
    const utterances = rows.reduce((n, r) => n + (r.utterances || 0), 0);
    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="choose-runs">
            <DialogTitle id="choose-runs" sx={{ typography: 'h4' }}>Choose runs</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Agreement counts only the runs you pick. A training or test run counts only if you pick it.</Typography>
                {runs.map((r) => (
                    <FormControlLabel
                        key={r.id}
                        sx={{ display: 'flex', alignItems: 'flex-start', m: 0, py: 0.75 }}
                        control={<Checkbox checked={chosen.has(r.id)} onChange={() => setChosen((c) => { const n = new Set(c); if (n.has(r.id)) n.delete(r.id); else n.add(r.id); return n; })} sx={{ pt: 0.25 }} />}
                        label={(
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {r.kind === 'import'
                                        ? `Imported run · ${r.status} · ${fmt(r.lessons)} lessons · counts in re-annotator rows only`
                                        : `ADAS run · ${r.status} · ${fmt(r.lessons)} lessons · ${fmt(r.reliabilityLessons ?? 0)} in the reliability subset`}
                                </Typography>
                            </Box>
                        )}
                    />
                ))}
                <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600 }}>{rows.length} {rows.length === 1 ? 'run' : 'runs'} · {fmt(lessons)} lessons · {fmt(utterances)} utterances</Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button variant="outlined" onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={() => onApply([...chosen])} disabled={!chosen.size}>Apply</Button>
            </DialogActions>
        </Dialog>
    );
};

const LESSON_COLUMNS = [
    { key: 'human_human', label: 'Human–human' },
    { key: 'model_human', label: 'Model–human' },
    { key: 'llm_human', label: 'LLM–human' },
    { key: 'classifier_llm', label: 'Classifier–LLM' },
    { key: 'reannotators', label: 'Re-annotators' },
];
const lessonValue = (row, dim, key) => (key === 'reannotators' ? row[dim].reannotators?.kappa : row[dim][key]);

// /runs/agreement (wf-research-agreement 1–5, YES-269): Cohen's κ with % agreement and n for every pair of
// coders, in the runs the researcher picks; the picks are in the URL. Counts and κ only, no text.
const Agreement = () => {
    const [params, setParams] = useSearchParams();
    const picked = useMemo(() => (params.get('runs') || '').split(',').filter(Boolean), [params]);
    const reference = params.get('reference') === 'final' ? 'final' : 'annotation';
    const [runs, setRuns] = useState(null);
    const [data, setData] = useState(null);
    const [status, setStatus] = useState('idle');
    const [choosing, setChoosing] = useState(false);
    const [codeDim, setCodeDim] = useState('rc');
    const [sort, setSort] = useState({ key: 'model_human', dir: 'asc' });
    const [allLessons, setAllLessons] = useState(false);

    useEffect(() => {
        listRuns().then(setRuns).catch(() => setRuns([]));
    }, []);

    const load = useCallback(async () => {
        if (!picked.length) { setData(null); setStatus('idle'); return; }
        setStatus('loading');
        try {
            setData(await getAgreement({ runs: picked, reference }));
            setStatus('ready');
        } catch (err) {
            setStatus('error');
        }
    }, [picked, reference]);

    useEffect(() => {
        load();
    }, [load]);

    const update = (next) => {
        const p = new URLSearchParams(params);
        Object.entries(next).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
        setParams(p, { replace: true });
    };

    const ceiling = data?.overall.find((r) => r.comparison === 'human_human');
    const lessons = useMemo(() => {
        const list = [...(data?.perLesson || [])];
        list.sort((a, b) => {
            const va = lessonValue(a, 'rc', sort.key);
            const vb = lessonValue(b, 'rc', sort.key);
            if (va == null) return 1;
            if (vb == null) return -1;
            return sort.dir === 'asc' ? va - vb : vb - va;
        });
        return list;
    }, [data, sort]);

    const download = async () => {
        const { blob, fileName } = await exportAgreementCsv({ runs: picked, reference });
        saveFile(blob, fileName || 'adas-agreement.csv');
    };

    const runName = (id) => runs?.find((r) => r.id === id)?.name || id;

    return (
        <Box sx={{ maxWidth: 1240, mx: 'auto' }}>
            <BackToRuns />
            <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'flex-end' }, justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mt: 0.5 }}>
                <Box>
                    <Typography variant="h2" component="h1">Agreement</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 760 }}>
                        How often pairs of coders give the same code, in the runs you pick. Human–human on the reliability subset is the ceiling.
                    </Typography>
                </Box>
                <Button variant="outlined" startIcon={<Download />} onClick={download} disabled={status !== 'ready'}>Download CSV</Button>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', my: 2.5 }}>
                {picked.map((id) => (
                    <Chip key={id} label={runName(id)} onDelete={() => update({ runs: picked.filter((r) => r !== id).join(',') })} />
                ))}
                <Button variant={picked.length ? 'text' : 'contained'} onClick={() => setChoosing(true)} disabled={!runs}>Choose runs</Button>
                <Box sx={{ flexGrow: 1 }} />
                <Typography id="human-label" variant="body2" color="text.secondary">Human label</Typography>
                <ToggleButtonGroup size="small" exclusive value={reference} onChange={(e, v) => v && update({ reference: v === 'final' ? 'final' : '' })} aria-labelledby="human-label">
                    <ToggleButton value="annotation" sx={{ textTransform: 'none' }}>Annotation label</ToggleButton>
                    <ToggleButton value="final" sx={{ textTransform: 'none' }}>Final label</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {status === 'idle' && (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" sx={{ maxWidth: 560, mx: 'auto' }}>
                        Pick the runs to measure. Only the runs you pick count, so a training run or a run still being tested never enters the numbers unless you add it.
                    </Typography>
                </Paper>
            )}
            {status === 'error' && <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>Couldn’t compute the agreement.</Alert>}
            {status === 'loading' && <Skeleton variant="rounded" height={360} />}

            {status === 'ready' && data && (
                <Box sx={{ display: 'grid', gap: 2.5 }}>
                    <Paper component="section" aria-label="Overall" sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant="h5" component="h2">Overall</Typography>
                        <Typography variant="caption" color="text.secondary">Cohen’s κ · % agreement · n</Typography>
                        {data.reference === 'final' && data.finalPending > 0 && (
                            <Alert severity="info" icon={false} sx={{ mt: 1.5 }}>{fmt(data.finalPending)} utterances have no final label yet and keep their annotation label.</Alert>
                        )}
                        <TableContainer sx={{ mt: 1.5, overflowX: 'auto' }}>
                            <Table size="small" aria-label="Overall agreement" sx={{ minWidth: 820 }}>
                                <TableHead>
                                    <TableRow>{['Comparison', 'Measured on', 'UT κ', 'UT %', 'RC κ', 'RC %', 'n'].map((h) => <TableCell key={h}>{h}</TableCell>)}</TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.overall.map((r, i) => {
                                        const def = ROWS[r.comparison];
                                        const label = typeof def.label === 'function' ? def.label(r) : def.label;
                                        const sub = r.comparison === 'llm_human_model';
                                        const shaded = ['model_human', 'llm_human', 'llm_human_model'].includes(r.comparison);
                                        return (
                                            <TableRow key={`${r.comparison}-${i}`} sx={{ bgcolor: shaded && reference === 'final' ? 'action.hover' : 'transparent' }}>
                                                <TableCell sx={{ fontWeight: sub ? 400 : 600, pl: sub ? 4 : 2, color: sub ? 'text.secondary' : 'text.primary' }}>{label}</TableCell>
                                                <TableCell sx={{ color: 'text.secondary' }}>{def.on(r, reference)}</TableCell>
                                                {r.waiting ? (
                                                    <TableCell colSpan={5} sx={{ color: 'text.secondary', fontStyle: 'italic' }}>{WAITING[r.waiting.reason](r.waiting)}</TableCell>
                                                ) : (
                                                    <>
                                                        <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{fmtK(r.ut.kappa)}<KappaBar kappa={r.ut.kappa} ceiling={ceiling?.ut?.kappa} /></TableCell>
                                                        <TableCell>{pct(r.ut.agreement)}</TableCell>
                                                        <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{fmtK(r.rc.kappa)}<KappaBar kappa={r.rc.kappa} ceiling={ceiling?.rc?.kappa} /></TableCell>
                                                        <TableCell>{pct(r.rc.agreement)}</TableCell>
                                                        <TableCell>{fmt(r.ut.n)}</TableCell>
                                                    </>
                                                )}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1 }}>
                            {ceiling?.waiting
                                ? 'Bars run from 0 to 1; the ceiling tick appears once the first reliability-subset lesson has both annotations.'
                                : 'Bars run from 0 to 1; the dark tick is the human–human ceiling. Re-annotator agreement on flagged items is lower by design: those items were selected as hard.'}
                        </Typography>
                    </Paper>

                    <Paper component="section" aria-label="Per code" sx={{ p: { xs: 2, sm: 3 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                            <Box>
                                <Typography variant="h5" component="h2">Per code</Typography>
                                <Typography variant="caption" color="text.secondary">κ for each code against all the others</Typography>
                            </Box>
                            <Tabs value={codeDim} onChange={(e, v) => setCodeDim(v)} aria-label="Dimension">
                                <Tab value="ut" label="UT" />
                                <Tab value="rc" label="RC" />
                            </Tabs>
                        </Box>
                        <TableContainer sx={{ mt: 1.5, overflowX: 'auto' }}>
                            <Table size="small" aria-label="Per code agreement" sx={{ minWidth: 720 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Code</TableCell><TableCell>n</TableCell>
                                        {LESSON_COLUMNS.map((c) => <TableCell key={c.key}>{c.label}</TableCell>)}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.perCode[codeDim].map((r) => (
                                        <TableRow key={r.code}>
                                            <TableCell><strong>{r.code}</strong> <Typography component="span" variant="body2" color="text.secondary">{(codeDim === 'rc' ? RC_NAMES : UT_NAMES)[r.code]}</Typography></TableCell>
                                            <TableCell>{fmt(r.n)}</TableCell>
                                            {LESSON_COLUMNS.map((c) => <TableCell key={c.key}>{fmtK(r[c.key])}</TableCell>)}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1 }}>
                            n: utterances with that code in the {reference} label. Re-annotators: flagged items only. One code against the rest, so codes with few utterances give unstable κ.
                        </Typography>
                    </Paper>

                    <Paper component="section" aria-label="Per lesson" sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant="h5" component="h2">Per lesson · RC κ</Typography>
                        <Typography variant="caption" color="text.secondary">{lessons.length} lessons · sorted by {LESSON_COLUMNS.find((c) => c.key === sort.key).label}, {sort.dir === 'asc' ? 'lowest' : 'highest'} first</Typography>
                        <TableContainer sx={{ mt: 1.5, overflowX: 'auto' }}>
                            <Table size="small" aria-label="Per lesson agreement" sx={{ minWidth: 820 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Lesson</TableCell><TableCell>Utterances</TableCell><TableCell>Reliability</TableCell>
                                        {LESSON_COLUMNS.map((c) => (
                                            <TableCell key={c.key} sortDirection={sort.key === c.key ? sort.dir : false}>
                                                <TableSortLabel active={sort.key === c.key} direction={sort.key === c.key ? sort.dir : 'asc'} onClick={() => setSort((s) => ({ key: c.key, dir: s.key === c.key && s.dir === 'asc' ? 'desc' : 'asc' }))}>
                                                    {c.label}
                                                </TableSortLabel>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(allLessons ? lessons : lessons.slice(0, 6)).map((l) => (
                                        <TableRow key={l.lessonId}>
                                            <TableCell><strong>{l.code}</strong><Typography variant="caption" component="p" color="text.secondary">{l.runName}</Typography></TableCell>
                                            <TableCell>{fmt(l.utterances)}</TableCell>
                                            <TableCell>{l.reliability ? 'yes' : '—'}</TableCell>
                                            {LESSON_COLUMNS.map((c) => (
                                                <TableCell key={c.key}>
                                                    {fmtK(lessonValue(l, 'rc', c.key))}
                                                    {c.key === 'reannotators' && l.rc.reannotators && <Typography component="span" variant="caption" color="text.secondary"> n {l.rc.reannotators.n}</Typography>}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {lessons.length > 6 && !allLessons && <Button onClick={() => setAllLessons(true)} sx={{ mt: 1 }}>Show all {lessons.length} lessons</Button>}
                        <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1 }}>
                            Click a column to sort. Human–human only on reliability-subset lessons. On those lessons model–human and LLM–human use the primary annotator’s label.
                        </Typography>
                    </Paper>
                </Box>
            )}

            {choosing && runs && (
                <ChooseRuns runs={runs} picked={picked} onClose={() => setChoosing(false)} onApply={(ids) => { setChoosing(false); update({ runs: ids.join(',') }); }} />
            )}
        </Box>
    );
};

export default Agreement;
